import { useCallback, useMemo } from "react";
import { SHELF_IDS, type ShelfId } from "@hybrid-iptv/tmdb-client";
import type { SeriesItem, VodItem } from "@hybrid-iptv/types";
import { api, runsOnDevice } from "../api/client.js";
import { providerBody } from "../epg/api.js";
import { primaryItems } from "../library/duplicates.js";
import { useVodCatalog } from "../library/vodHooks.js";
import { useCatalogGenres, useCatalogQuery } from "../library/useCatalogQuery.js";
import type { CatalogQuery, ShelfFillRequest } from "../library/catalogQuery.js";
import { buildTitleIndex, normalizeTitle, type TitleIndex, type TmdbListEntry } from "./catalogTitleMatch.js";
import { buildShelfCandidates, pickShelfFill, shelvesForCategory } from "./shelfFill.js";

/**
 * The catalog side of Home: what its rails need to know about the provider's
 * titles, without the page holding the whole catalog in the APK (stage B).
 *
 * On the device only the titles Home can feature are asked for: those with the
 * TMDB ids of its lists, and those without a TMDB id whose title key matches a
 * listed title (matchTmdbTitles' two ways to match). Both come back in catalog
 * order, so the maps and title index built from them give what the whole
 * catalog gave for those titles. The genre-row top-up runs natively
 * (CatalogStore.shelfFill mirrors pickShelfFill). The website keeps the
 * in-memory catalog the server sends.
 */

type Item = VodItem | SeriesItem;
export type ShelfFill = (shelf: ShelfId, used: ReadonlySet<number>, count: number, seed: string) => Promise<Item[]>;

export interface HomeCatalogInput {
  sourceId: string;
  enabled: boolean;
  media: "movie" | "tv";
  /** TMDB titles Home may feature, matched by id or by title and year. */
  movieTitles: readonly TmdbListEntry[];
  tvTitles: readonly TmdbListEntry[];
  /** More TMDB ids matched by id only (a tile opened from elsewhere). */
  movieIds: readonly number[];
  tvIds: readonly number[];
  favMovieRefs: readonly string[];
  favSeriesRefs: readonly string[];
}

export interface HomeCatalog {
  moviesById: Map<number, VodItem>;
  seriesById: Map<number, SeriesItem>;
  movieTitleIndex: TitleIndex<VodItem>;
  seriesTitleIndex: TitleIndex<SeriesItem>;
  /** The catalog has titles of this kind and the lookups above have answered. */
  moviesReady: boolean;
  seriesReady: boolean;
  /** Favourite movies and series found in the catalog (any order). */
  favMovies: VodItem[];
  favSeries: SeriesItem[];
  /** Changes whenever fillShelf would answer differently. */
  fillShelf: ShelfFill;
  isLoading: boolean;
  error: unknown;
}

const LOOKUP_LIMIT = 5000;
const NO_VOD: VodItem[] = [];
const NO_SERIES: SeriesItem[] = [];

/** TMDB id → the first catalog title with it, as useCatalogMovieMatches builds it. */
function byTmdbId<T extends Item>(items: readonly T[]): Map<number, T> {
  const map = new Map<number, T>();
  for (const item of items) {
    if (item.tmdbId && Number.isInteger(item.tmdbId) && item.tmdbId > 0 && !map.has(item.tmdbId)) map.set(item.tmdbId, item);
  }
  return map;
}

function uniqueSorted<T extends string | number>(values: Iterable<T>): T[] {
  return [...new Set(values)].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

/** The lookups for one kind, on the device. */
function useDeviceLookups<T extends Item>(
  sourceId: string,
  kind: "vod" | "series",
  enabled: boolean,
  titles: readonly TmdbListEntry[],
  extraIds: readonly number[],
  favRefs: readonly string[],
) {
  const ids = useMemo(() => uniqueSorted([...titles.map((t) => t.id), ...extraIds]), [titles, extraIds]);
  const keys = useMemo(() => uniqueSorted(titles.map((t) => normalizeTitle(t.title)).filter(Boolean)), [titles]);
  const favs = useMemo(() => uniqueSorted(favRefs), [favRefs]);
  const idQuery = useMemo<CatalogQuery>(() => ({ kind, tmdbIds: ids, limit: LOOKUP_LIMIT }), [kind, ids]);
  const keyQuery = useMemo<CatalogQuery>(() => ({ kind, titleKeys: keys, limit: LOOKUP_LIMIT }), [kind, keys]);
  const favQuery = useMemo<CatalogQuery>(() => ({ kind, ids: favs, limit: Math.max(favs.length, 1) }), [kind, favs]);
  const countQuery = useMemo<CatalogQuery>(() => ({ kind, limit: 0 }), [kind]);
  const byId = useCatalogQuery<T>(sourceId, idQuery, enabled && ids.length > 0);
  const byKey = useCatalogQuery<T>(sourceId, keyQuery, enabled && keys.length > 0);
  const fav = useCatalogQuery<T>(sourceId, favQuery, enabled && favs.length > 0);
  const count = useCatalogQuery<T>(sourceId, countQuery, enabled);
  const none = NO_VOD as unknown as T[];
  const idItems = (ids.length > 0 && byId.data?.items) || none;
  const keyItems = (keys.length > 0 && byKey.data?.items) || none;
  const favItems = (favs.length > 0 && fav.data?.items) || none;
  const answered = (ids.length === 0 || byId.data !== undefined) && (keys.length === 0 || byKey.data !== undefined);
  const parts = [
    { on: ids.length > 0, q: byId },
    { on: keys.length > 0, q: byKey },
    { on: favs.length > 0, q: fav },
    { on: true, q: count },
  ];
  return {
    idItems,
    keyItems,
    favItems,
    ready: enabled && answered && (count.data?.total ?? 0) > 0,
    isLoading: enabled && parts.some(({ on, q }) => on && q.isLoading),
    error: enabled ? parts.find(({ on, q }) => on && q.isError)?.q.error : undefined,
  };
}

export function useHomeCatalog(input: HomeCatalogInput): HomeCatalog {
  const device = runsOnDevice();
  const { sourceId, enabled, media } = input;

  // --- Website: the whole catalog in memory, exactly as before stage B.
  const vod = useVodCatalog(sourceId, enabled && !device);
  const webMovies = !device && enabled ? vod.data?.vod ?? NO_VOD : NO_VOD;
  const webSeries = !device && enabled ? vod.data?.series ?? NO_SERIES : NO_SERIES;
  // One entry per title (duplicates.ts): the lookups and the genre rows see the best stream of each;
  // My List below keeps every copy, so a favourite saved on another stream still resolves.
  const bestMovies = useMemo(() => primaryItems(webMovies), [webMovies]);
  const bestSeries = useMemo(() => primaryItems(webSeries), [webSeries]);
  const webCandidates = useMemo(
    () => buildShelfCandidates<Item>(media === "tv" ? bestSeries : bestMovies, media, SHELF_IDS),
    [media, bestMovies, bestSeries],
  );

  // --- Device: ask only for the titles Home can feature.
  const movies = useDeviceLookups<VodItem>(sourceId, "vod", enabled && device, input.movieTitles, input.movieIds, input.favMovieRefs);
  const series = useDeviceLookups<SeriesItem>(sourceId, "series", enabled && device, input.tvTitles, input.tvIds, input.favSeriesRefs);
  const genres = useCatalogGenres(sourceId, media === "tv" ? "series" : "vod", enabled && device);

  const moviesById = useMemo(() => byTmdbId(device ? movies.idItems : bestMovies), [device, movies.idItems, bestMovies]);
  const seriesById = useMemo(() => byTmdbId(device ? series.idItems : bestSeries), [device, series.idItems, bestSeries]);
  const movieTitleIndex = useMemo(() => buildTitleIndex(device ? movies.keyItems : bestMovies), [device, movies.keyItems, bestMovies]);
  const seriesTitleIndex = useMemo(() => buildTitleIndex(device ? series.keyItems : bestSeries), [device, series.keyItems, bestSeries]);

  const favMovieSet = useMemo(() => new Set(input.favMovieRefs), [input.favMovieRefs]);
  const favSeriesSet = useMemo(() => new Set(input.favSeriesRefs), [input.favSeriesRefs]);
  const favMovies = useMemo(
    () => (device ? movies.favItems : webMovies).filter((m) => favMovieSet.has(m.id)),
    [device, movies.favItems, webMovies, favMovieSet],
  );
  const favSeries = useMemo(
    () => (device ? series.favItems : webSeries).filter((s) => favSeriesSet.has(s.id)),
    [device, series.favItems, webSeries, favSeriesSet],
  );

  const genreData = genres.data;
  const fillShelf = useCallback<ShelfFill>(
    async (shelf, used, count, seed) => {
      if (!device) return pickShelfFill(webCandidates.get(shelf) ?? [], used, count, seed);
      const categories = (genreData ?? []).filter((id) => shelvesForCategory(id, media, SHELF_IDS).includes(shelf));
      if (count <= 0 || categories.length === 0) return [];
      const request: ShelfFillRequest = { kind: media === "tv" ? "series" : "vod", categories, used: [...used], count, seed };
      // Locked categories never reach `categories`: useCatalogGenres leaves them out.
      return api.post<Item[]>(`/api/sources/${sourceId}/vod/shelf-fill`, { ...providerBody(), request, excludeCategories: [] });
    },
    [device, webCandidates, genreData, media, sourceId],
  );

  if (!device) {
    return {
      moviesById, seriesById, movieTitleIndex, seriesTitleIndex,
      moviesReady: webMovies.length > 0,
      seriesReady: webSeries.length > 0,
      favMovies, favSeries, fillShelf,
      isLoading: vod.isLoading,
      error: vod.error,
    };
  }
  // The genre-row top-up needs the category list as well.
  const genresReady = genreData !== undefined;
  return {
    moviesById, seriesById, movieTitleIndex, seriesTitleIndex,
    moviesReady: movies.ready && (media === "tv" || genresReady),
    seriesReady: series.ready && (media === "movie" || genresReady),
    favMovies, favSeries, fillShelf,
    isLoading: movies.isLoading || series.isLoading,
    error: movies.error ?? series.error ?? genres.error,
  };
}
